import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  changePassword,
  type MyProfileDTO,
} from '@/api/adapters/my-profile';
import { ApiError } from '@/api/error';
import {
  passwordChangeSchema,
  type PasswordChangeFormValues,
} from '@/features/my-profile/schemas/password-change.schema';
import { MY_PROFILE_QUERY_KEY } from '@/shared/hooks/use-my-profile';
import { modal } from '@/app/store/modal.store';
import { toast } from '@/app/store/toast.store';

interface PasswordChangeFormProps {
  onSuccess: () => void;
}

export function PasswordChangeForm({ onSuccess }: PasswordChangeFormProps) {
  const { t } = useTranslation('myProfile');
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting, isValid },
  } = useForm<PasswordChangeFormValues>({
    resolver: zodResolver(passwordChangeSchema),
    mode: 'onTouched',
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const currentRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    currentRef.current?.focus();
  }, []);
  const currentPasswordReg = register('currentPassword');

  // ── Per-field error messages ──
  const currentError = errors.currentPassword?.message ?? null;
  const newError = errors.newPassword?.message ?? null;
  const confirmError = errors.confirmPassword?.message ?? null;

  const currentErrorText = currentError ? t(currentError) : null;
  const newErrorText = newError ? t(newError) : null;
  const confirmErrorText = confirmError ? t(confirmError) : null;

  const onSubmit = handleSubmit(async (data) => {
    try {
      const { passwordChangedAt } = await changePassword(
        data.currentPassword,
        data.newPassword,
      );
      // Optimistic + invalidate, matching the phone-change pattern.
      queryClient.setQueryData<MyProfileDTO | undefined>(MY_PROFILE_QUERY_KEY, (prev) =>
        prev ? { ...prev, passwordChangedAt: passwordChangedAt.slice(0, 10) } : prev,
      );
      queryClient.invalidateQueries({ queryKey: MY_PROFILE_QUERY_KEY });
      toast.success(t('passwordChange.messages.saveSuccess'));
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError) {
        switch (err.code) {
          case 'AUTH_PASSWORD_MISMATCH':
            setError('currentPassword', {
              type: 'server',
              message: 'myProfile:passwordChange.errors.currentMismatch',
            });
            break;
          case 'AUTH_PASSWORD_POLICY':
            setError('newPassword', {
              type: 'server',
              message: 'myProfile:passwordChange.errors.policy',
            });
            break;
          case 'PASSWORD_SAME_AS_CURRENT':
            setError('newPassword', {
              type: 'server',
              message: 'myProfile:passwordChange.errors.sameAsCurrent',
            });
            break;
          case 'AUTH_PASSWORD_REUSE':
            setError('newPassword', {
              type: 'server',
              message: 'myProfile:passwordChange.errors.reused',
            });
            break;
          case 'UNAUTHENTICATED':
            // httpClient interceptor handles the logout/redirect; stay quiet.
            break;
          default:
            // PRD §8.2 — system failure shows a popup, not a toast.
            modal.alert(
              t('passwordChange.messages.saveFailure'),
              t('passwordChange.errors.generic'),
            );
        }
      } else {
        modal.alert(
          t('passwordChange.messages.saveFailure'),
          t('passwordChange.errors.generic'),
        );
      }
    }
  });

  const errorIdCurrent = 'password-change-error-current';
  const errorIdNew = 'password-change-error-new';
  const errorIdConfirm = 'password-change-error-confirm';

  return (
    <form className="card__body inline-edit" onSubmit={onSubmit} noValidate>
      <div className="form-field">
        <label htmlFor="password-current">{t('passwordChange.fields.current')}</label>
        <input
          id="password-current"
          type="password"
          autoComplete="current-password"
          aria-invalid={currentErrorText !== null}
          aria-describedby={currentErrorText ? errorIdCurrent : undefined}
          name={currentPasswordReg.name}
          onChange={currentPasswordReg.onChange}
          onBlur={currentPasswordReg.onBlur}
          ref={(el) => {
            currentPasswordReg.ref(el);
            currentRef.current = el;
          }}
        />
        {currentErrorText && (
          <div id={errorIdCurrent} className="form-field__error" role="alert">
            {currentErrorText}
          </div>
        )}
      </div>
      <div className="form-field">
        <label htmlFor="password-new">{t('passwordChange.fields.new')}</label>
        <input
          id="password-new"
          type="password"
          autoComplete="new-password"
          aria-invalid={newErrorText !== null}
          aria-describedby={newErrorText ? errorIdNew : undefined}
          {...register('newPassword')}
        />
        {newErrorText && (
          <div id={errorIdNew} className="form-field__error" role="alert">
            {newErrorText}
          </div>
        )}
      </div>
      <div className="form-field">
        <label htmlFor="password-confirm">{t('passwordChange.fields.confirm')}</label>
        <input
          id="password-confirm"
          type="password"
          autoComplete="new-password"
          aria-invalid={confirmErrorText !== null}
          aria-describedby={confirmErrorText ? errorIdConfirm : undefined}
          {...register('confirmPassword')}
        />
        {confirmErrorText && (
          <div id={errorIdConfirm} className="form-field__error" role="alert">
            {confirmErrorText}
          </div>
        )}
      </div>
      <div className="form-actions">
        <button
          type="submit"
          className="btn btn--primary"
          disabled={!isValid || isSubmitting}
          aria-busy={isSubmitting}
        >
          {t('passwordChange.actions.save')}
        </button>
      </div>
    </form>
  );
}
